/**
 * Copyright (c) 2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Gianluca Tomasello <giagitom@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { Unit } from '../../../mol-model/structure';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { Segmentation } from '../../../mol-data/int';
import { isNucleic } from '../../../mol-model/structure/model/types';
import { addSphere } from '../../../mol-geo/geometry/mesh/builder/sphere';
import { UnitsMeshParams, UnitsMeshVisual, UnitsSpheresParams, UnitsSpheresVisual } from '../units-visual';
import { NucleotideLocationIterator, getNucleotideElementLoci, eachNucleotideElement, getNucleotideBaseType, createNucleicIndices, setSugarIndices, hasSugarIndices, setPurinIndices, hasPurinIndices, setPyrimidineIndices, hasPyrimidineIndices } from './util/nucleotide';
import { BaseGeometry } from '../../../mol-geo/geometry/base';
import { Sphere3D } from '../../../mol-math/geometry';
import { Spheres } from '../../../mol-geo/geometry/spheres/spheres';
import { sphereVertexCount } from '../../../mol-geo/primitive/sphere';
import { SpheresBuilder } from '../../../mol-geo/geometry/spheres/spheres-builder';
var pTrace = Vec3();
var pN1 = Vec3();
var pC2 = Vec3();
var pN3 = Vec3();
var pC4 = Vec3();
var pC5 = Vec3();
var pC6 = Vec3();
var pN7 = Vec3();
var pC8 = Vec3();
var pN9 = Vec3();
var pC1_1 = Vec3();
var pC2_1 = Vec3();
var pC3_1 = Vec3();
var pC4_1 = Vec3();
var pO4_1 = Vec3();
export var NucleotideAtomicElementParams = __assign(__assign(__assign({}, UnitsMeshParams), UnitsSpheresParams), { sizeFactor: PD.Numeric(0.3, { min: 0, max: 10, step: 0.01 }), detail: PD.Numeric(0, { min: 0, max: 3, step: 1 }, BaseGeometry.CustomQualityParamInfo), tryUseImpostor: PD.Boolean(true) });
export function NucleotideAtomicElementVisual(materialId, structure, props, webgl) {
    return props.tryUseImpostor && webgl && webgl.extensions.fragDepth
        ? NucleotideAtomicElementImpostorVisual(materialId)
        : NucleotideAtomicElementMeshVisual(materialId);
}
function createNucleotideAtomicElementImpostor(ctx, unit, structure, theme, props, spheres) {
    if (!Unit.isAtomic(unit))
        return Spheres.createEmpty(spheres);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Spheres.createEmpty(spheres);
    var spheresCountEstimate = nucleotideElementCount * 15; // 15 is the average purine (17) & pirimidine (13) bonds
    var builder = SpheresBuilder.create(spheresCountEstimate, spheresCountEstimate / 4, spheres);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                setSugarIndices(idx, unit, residueIndex);
                if (hasSugarIndices(idx)) {
                    pos(idx.C1_1, pC1_1);
                    pos(idx.C2_1, pC2_1);
                    pos(idx.C3_1, pC3_1);
                    pos(idx.C4_1, pC4_1);
                    pos(idx.O4_1, pO4_1);
                    // trace cylinder
                    pos(idx.trace, pTrace);
                    builder.add(pTrace[0], pTrace[1], pTrace[2], i);
                    // sugar ring
                    builder.add(pC3_1[0], pC3_1[1], pC3_1[2], i);
                    builder.add(pC4_1[0], pC4_1[1], pC4_1[2], i);
                    builder.add(pO4_1[0], pO4_1[1], pO4_1[2], i);
                    builder.add(pC1_1[0], pC1_1[1], pC1_1[2], i);
                    builder.add(pC2_1[0], pC2_1[1], pC2_1[2], i);
                }
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    setPurinIndices(idx, unit, residueIndex);
                    if (hasPurinIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        pos(idx.N7, pN7);
                        pos(idx.C8, pC8);
                        pos(idx.N9, pN9);
                        // base ring
                        builder.add(pN9[0], pN9[1], pN9[2], i);
                        builder.add(pC8[0], pC8[1], pC8[2], i);
                        builder.add(pN7[0], pN7[1], pN7[2], i);
                        builder.add(pC5[0], pC5[1], pC5[2], i);
                        builder.add(pC6[0], pC6[1], pC6[2], i);
                        builder.add(pN1[0], pN1[1], pN1[2], i);
                        builder.add(pC2[0], pC2[1], pC2[2], i);
                        builder.add(pN3[0], pN3[1], pN3[2], i);
                        builder.add(pC4[0], pC4[1], pC4[2], i);
                    }
                }
                else if (isPyrimidine) {
                    setPyrimidineIndices(idx, unit, residueIndex);
                    if (hasPyrimidineIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        // base ring
                        builder.add(pN1[0], pN1[1], pN1[2], i);
                        builder.add(pC6[0], pC6[1], pC6[2], i);
                        builder.add(pC5[0], pC5[1], pC5[2], i);
                        builder.add(pC4[0], pC4[1], pC4[2], i);
                        builder.add(pN3[0], pN3[1], pN3[2], i);
                        builder.add(pC2[0], pC2[1], pC2[2], i);
                    }
                }
                ++i;
            }
        }
    }
    var c = builder.getSpheres();
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, 1 * props.sizeFactor);
    c.setBoundingSphere(sphere);
    return c;
}
export function NucleotideAtomicElementImpostorVisual(materialId) {
    return UnitsSpheresVisual({
        defaultProps: PD.getDefaultValues(NucleotideAtomicElementParams),
        createGeometry: createNucleotideAtomicElementImpostor,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor);
        },
        mustRecreate: function (structureGroup, props, webgl) {
            return !props.tryUseImpostor || !webgl;
        }
    }, materialId);
}
function createNucleotideAtomicElementMesh(ctx, unit, structure, theme, props, mesh) {
    if (!Unit.isAtomic(unit))
        return Mesh.createEmpty(mesh);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Mesh.createEmpty(mesh);
    var sizeFactor = props.sizeFactor, detail = props.detail;
    var vertexCount = nucleotideElementCount * sphereVertexCount(detail);
    var builderState = MeshBuilder.createState(vertexCount, vertexCount / 2, mesh);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var radius = 1 * sizeFactor;
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                builderState.currentGroup = i;
                setSugarIndices(idx, unit, residueIndex);
                if (hasSugarIndices(idx)) {
                    pos(idx.C1_1, pC1_1);
                    pos(idx.C2_1, pC2_1);
                    pos(idx.C3_1, pC3_1);
                    pos(idx.C4_1, pC4_1);
                    pos(idx.O4_1, pO4_1);
                    // trace cylinder
                    pos(idx.trace, pTrace);
                    addSphere(builderState, pTrace, radius, detail);
                    // sugar ring
                    addSphere(builderState, pC4_1, radius, detail);
                    addSphere(builderState, pO4_1, radius, detail);
                    addSphere(builderState, pC1_1, radius, detail);
                    addSphere(builderState, pC2_1, radius, detail);
                    addSphere(builderState, pC3_1, radius, detail);
                }
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    setPurinIndices(idx, unit, residueIndex);
                    if (hasPurinIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        pos(idx.N7, pN7);
                        pos(idx.C8, pC8);
                        pos(idx.N9, pN9);
                        // base ring
                        addSphere(builderState, pC8, radius, detail);
                        addSphere(builderState, pN7, radius, detail);
                        addSphere(builderState, pC5, radius, detail);
                        addSphere(builderState, pC6, radius, detail);
                        addSphere(builderState, pN1, radius, detail);
                        addSphere(builderState, pC2, radius, detail);
                        addSphere(builderState, pN3, radius, detail);
                        addSphere(builderState, pC4, radius, detail);
                        addSphere(builderState, pC5, radius, detail);
                        addSphere(builderState, pN9, radius, detail);
                    }
                }
                else if (isPyrimidine) {
                    setPyrimidineIndices(idx, unit, residueIndex);
                    if (hasPyrimidineIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        // base ring
                        addSphere(builderState, pC6, radius, detail);
                        addSphere(builderState, pC5, radius, detail);
                        addSphere(builderState, pC4, radius, detail);
                        addSphere(builderState, pN3, radius, detail);
                        addSphere(builderState, pC2, radius, detail);
                        addSphere(builderState, pN1, radius, detail);
                    }
                }
                ++i;
            }
        }
    }
    var m = MeshBuilder.getMesh(builderState);
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, 1 * props.sizeFactor);
    m.setBoundingSphere(sphere);
    return m;
}
export function NucleotideAtomicElementMeshVisual(materialId) {
    return UnitsMeshVisual({
        defaultProps: PD.getDefaultValues(NucleotideAtomicElementParams),
        createGeometry: createNucleotideAtomicElementMesh,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor ||
                newProps.detail !== currentProps.detail);
        },
        mustRecreate: function (structureGroup, props, webgl) {
            return props.tryUseImpostor && !!webgl;
        }
    }, materialId);
}
